import { useCallback, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { AsH1, TitleLG } from '@/atoms/Text'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { AdditionalMusicVideo } from '../types'

interface AdditionalMusicVideosSectionProps {
  additionalMusicVideos: AdditionalMusicVideo[]
}

export const AdditionalMusicVideosSection: React.FC<AdditionalMusicVideosSectionProps> = ({
  additionalMusicVideos,
}) => {
  const [showTrailerModal, setShowTrailerModal] = useState(false)
  const [modalTrailerUrl, setTrailerUrl] = useState('')
  const track = useSafeTrack()

  const handleTrailerClick = useCallback(
    (trailerUrl: string) => {
      setShowTrailerModal(true)
      setTrailerUrl(trailerUrl)
      track('Music Page Music Video Thumbnail Clicked', trailerUrl)
    },
    [track],
  )

  const handleModalClick = useCallback(() => {
    setShowTrailerModal(false)
    setTrailerUrl('')
  }, [])

  return (
    <>
      {additionalMusicVideos.length > 0 &&
        additionalMusicVideos.map((additionalMusicVideo: AdditionalMusicVideo) => {
          return (
            <section key={additionalMusicVideo.title} className="core-gray-950 relative mt-10 w-full py-[72px]">
              <header>
                <TitleLG weight="bold" as={AsH1} className="px-4 sm:px-8 md:px-12 xl:px-16">
                  {additionalMusicVideo.title}
                </TitleLG>
              </header>

              <BasicCarousel containerClassName="px-4 sm:px-8 md:px-12 xl:px-16 py-4 md:py-4">
                {additionalMusicVideo.musicVideos.map((contentLink, idx) => (
                  <div
                    className="w-[250px] shrink-0 snap-center snap-always md:!w-[350px] lg:w-[400px] xl:w-[500px]"
                    key={`${contentLink?.id ?? contentLink?.videoUrl ?? contentLink?.imageUrl}-${idx}`}
                  >
                    <div className="mr-4 min-w-full">
                      <div className="relative cursor-pointer overflow-hidden rounded-lg duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.05]">
                        <div className={classNames(['relative w-full bg-black rounded-lg'])}>
                          <div
                            className="aspect-h-9 aspect-w-16 relative w-full"
                            onClick={() => handleTrailerClick(contentLink.videoUrl)}
                          >
                            <Image
                              src={getCloudinaryImageUrl({ path: contentLink.imageUrl ?? '', width: 800, height: 450 })}
                              className="absolute inset-0 h-full w-full rounded-lg object-cover"
                              alt={contentLink.alt as string}
                              width={800}
                              height={450}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </BasicCarousel>
            </section>
          )
        })}
      <YoutubeVideoModal youTubeUrl={modalTrailerUrl} handleClose={handleModalClick} showModal={showTrailerModal} />
    </>
  )
}
